import { ArrowBackIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  ButtonGroup,
  Container,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  ApiBulkExport,
  BulkExportType,
  BulkFileConfig,
  CompositeMapConfig,
  RorConfig,
} from "@nens/ldo/api-v1";
import {
  Breadcrumbs,
  BulkExportTypeDescription,
  ExportBulkFileFields,
  ExportCompositeMapsFields,
  ExportRorFields,
} from "@nens/ldo/components";

import { useBulkExportNew } from "./bulk-export-new-hook";

interface CoreProps {
  exportType: BulkExportType;
  defaultName: string;
  defaultDescription: string;
  defaultBulkConfig: BulkFileConfig;
  defaultCompositeConfig: CompositeMapConfig;
  defaultRorConfig: RorConfig;
  defaultExtra: Partial<ApiBulkExport>;
}

export function BulkExportNewCore({
  exportType,
  defaultName,
  defaultDescription,
  defaultBulkConfig,
  defaultCompositeConfig,
  defaultRorConfig,
  defaultExtra,
}: CoreProps) {
  const intl = useIntl();

  const {
    newExport,
    setNewExport,
    setLocation,
    listUrl,
    newExportConfigBulk,
    setNewExportConfigBulk,
    newExportConfigComposite,
    setNewExportConfigComposite,
    newExportConfigRor,
    setNewExportConfigRor,
    formValid,
    formSubmitting,
    submit,
    formErrorKeys,
  } = useBulkExportNew({
    exportType,
    name: defaultName,
    description: defaultDescription,
    bulkConfig: defaultBulkConfig,
    compositeConfig: defaultCompositeConfig,
    rorConfig: defaultRorConfig,
    extra: defaultExtra,
  });

  return (
    <Box w="100%">
      <Breadcrumbs
        crumbs={[
          { label: intl.formatMessage({ id: "pages.home", defaultMessage: "Home" }), href: "/" },
          {
            label: intl.formatMessage({
              id: "pages.export_scenarios",
              defaultMessage: "Export Scenarios",
            }),
            href: listUrl,
          },
          {
            label: intl.formatMessage({
              id: "export.create_new",
              defaultMessage: "Create New Export",
            }),
            href: `${listUrl}/new`,
          },
        ]}
      />
      <Container maxW={"7xl"} py={"24px"} justifyItems="center" w="100%">
        <Heading mb={4}>
          <BulkExportTypeDescription exportType={exportType} />
        </Heading>
        <ButtonGroup variant="outline">
          <Button
            onClick={() => setLocation(listUrl)}
            title={intl.formatMessage({
              id: "pages.export_scenarios.back",
              defaultMessage: "Back to exports",
            })}
            leftIcon={<ArrowBackIcon />}
          >
            <FormattedMessage id="pages.export_scenarios.back" defaultMessage="Back to exports" />
          </Button>
        </ButtonGroup>
        <FormControl my="4">
          <FormLabel fontWeight="semibold">
            <FormattedMessage id="pages.export_scenarios.new.name" defaultMessage="Name" />
          </FormLabel>
          <Input
            type="text"
            autoFocus
            value={newExport.name}
            onChange={(e) => setNewExport((theExport) => ({ ...theExport, name: e.target.value }))}
          />
          <FormHelperText>
            *<FormattedMessage id="misc.required_field" defaultMessage="Required field" />
          </FormHelperText>
        </FormControl>
        <FormControl my="4">
          <FormLabel fontWeight="semibold">
            <FormattedMessage
              id="pages.export_scenarios.new.description"
              defaultMessage="Description"
            />
          </FormLabel>
          <Textarea
            onChange={(e) =>
              setNewExport((theExport) => ({ ...theExport, description: e.target.value }))
            }
            value={newExport.description}
          />
        </FormControl>

        {exportType === "bulk_file" ? (
          <ExportBulkFileFields
            readonly={false}
            value={newExportConfigBulk}
            onChange={(partialConfig) =>
              setNewExportConfigBulk((config) => ({ ...config, ...partialConfig }))
            }
            formErrorKeys={formErrorKeys}
          />
        ) : null}

        {exportType === "composite_map" ? (
          <ExportCompositeMapsFields
            readonly={false}
            value={newExportConfigComposite}
            onChange={(partialConfig) =>
              setNewExportConfigComposite((config) => ({ ...config, ...partialConfig }))
            }
            formErrorKeys={formErrorKeys}
          />
        ) : null}

        {exportType === "ror" ? (
          <ExportRorFields
            readonly={false}
            value={newExportConfigRor}
            onChange={(partialConfig) =>
              setNewExportConfigRor((config) => ({ ...config, ...partialConfig }))
            }
            formErrorKeys={formErrorKeys}
          />
        ) : null}

        {defaultExtra.scenario_ids?.length ? (
          <Text fontWeight="semibold">
            Also cloning the {defaultExtra.scenario_ids?.length} scenarios attached to this export.
          </Text>
        ) : null}

        {formSubmitting ? (
          <Button variant={"solid"} isDisabled={true} cursor={"not-allowed"} size="lg" my="4">
            <FormattedMessage
              id="pages.export_scenarios.new.please_wait"
              defaultMessage="Please wait"
            />
          </Button>
        ) : (
          <Button
            variant={formValid ? "solid" : "outline"}
            isDisabled={!formValid}
            cursor={formValid ? "pointer" : "not-allowed"}
            onClick={formValid ? submit : undefined}
            size="lg"
            my="4"
          >
            <FormattedMessage
              id="pages.export_scenarios.new.submit"
              defaultMessage="Create Export"
            />
          </Button>
        )}
      </Container>
    </Box>
  );
}
